import * as THREE from 'three';
import { createNameLabel } from '../utils/NameLabel.js';

export class Shark {
    constructor(scene, position, type = 'normal') {
        this.scene = scene;
        this.type = type;
        
        if (type === 'greatwhite') {
            this.health = 300; // Much tankier
            this.maxHealth = 300;
            this.speed = 3; // Slower when far
            this.normalSpeed = 3;
            this.chargeSpeed = 15; // FAST when close!
            this.attackRange = 5;
            this.attackDamage = 35; // Big damage!
            this.essenceValue = 1000; // Reduced from 1500
            this.aggroRange = 50; // Aggros from further away
        } else {
            this.health = 100;
            this.maxHealth = 100;
            this.speed = 8;
            this.normalSpeed = 8;
            this.chargeSpeed = 8;
            this.attackRange = 3;
            this.attackDamage = 15;
            this.essenceValue = 50;
            this.aggroRange = 30;
        }
        
        this.lastAttackTime = 0;
        this.attackCooldown = 2;
        this.isDead = false;
        this.isCharging = false;
        
        this.createMesh(position);
        this.createHealthBar();
    }
    
    createMesh(position) {
        const sharkGroup = new THREE.Group();
        
        // Size and color based on type
        const size = this.type === 'greatwhite' ? 2.0 : 1.0; // Great Whites are 2x bigger
        const color = this.type === 'greatwhite' ? 0xE0E0E0 : 0x708090; // White vs grey
        const emissiveColor = this.type === 'greatwhite' ? 0xFFFFFF : 0x404040;
        
        // Body (streamlined)
        const bodyGeometry = new THREE.ConeGeometry(0.5 * size, 2.5 * size, 8);
        bodyGeometry.rotateX(Math.PI / 2); // Point forward
        const bodyMaterial = new THREE.MeshStandardMaterial({
            color: color,
            roughness: 0.6,
            metalness: 0.4,
            emissive: emissiveColor,
            emissiveIntensity: this.type === 'greatwhite' ? 0.1 : 0.2
        });
        const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
        sharkGroup.add(body);
        
        // Fins
        const finGeometry = new THREE.ConeGeometry(0.25 * size, 0.6 * size, 3);
        const finMaterial = new THREE.MeshStandardMaterial({
            color: this.type === 'greatwhite' ? 0xC0C0C0 : 0x606060,
            roughness: 0.7,
            metalness: 0.3
        });
        
        // Dorsal fin (top)
        const dorsalFin = new THREE.Mesh(finGeometry, finMaterial);
        dorsalFin.rotation.x = Math.PI;
        dorsalFin.position.set(0, 0.5 * size, 0);
        sharkGroup.add(dorsalFin);
        
        // Side fins
        const leftFin = new THREE.Mesh(finGeometry, finMaterial);
        leftFin.rotation.z = Math.PI / 2;
        leftFin.position.set(-0.4 * size, 0, 0.3 * size);
        sharkGroup.add(leftFin);
        
        const rightFin = new THREE.Mesh(finGeometry, finMaterial);
        rightFin.rotation.z = -Math.PI / 2;
        rightFin.position.set(0.4 * size, 0, 0.3 * size);
        sharkGroup.add(rightFin);
        
        // Tail
        const tailGeometry = new THREE.ConeGeometry(0.3 * size, 0.8 * size, 3);
        tailGeometry.rotateX(Math.PI / 2);
        const tail = new THREE.Mesh(tailGeometry, bodyMaterial);
        tail.position.z = -1.2 * size;
        sharkGroup.add(tail);
        
        // Eyes
        const eyeGeometry = new THREE.SphereGeometry(0.08 * size, 8, 8);
        const eyeMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
        
        const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
        leftEye.position.set(-0.2 * size, 0.1 * size, 0.8 * size);
        sharkGroup.add(leftEye);
        
        const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
        rightEye.position.set(0.2 * size, 0.1 * size, 0.8 * size);
        sharkGroup.add(rightEye);
        
        // Add name label
        const labelText = this.type === 'greatwhite' ? 'GREAT WHITE' : 'SHARK';
        const labelColor = this.type === 'greatwhite' ? '#ffffff' : '#a0a0a0';
        const label = createNameLabel(labelText, labelColor);
        sharkGroup.add(label);
        
        sharkGroup.position.copy(position);
        this.scene.add(sharkGroup);
        this.mesh = sharkGroup;
    }
    
    createHealthBar() {
        // Health bar background
        const barWidth = 2;
        const barHeight = 0.15;
        const barGeometry = new THREE.PlaneGeometry(barWidth, barHeight);
        const barBackMaterial = new THREE.MeshBasicMaterial({ color: 0x333333 });
        this.healthBarBack = new THREE.Mesh(barGeometry, barBackMaterial);
        this.healthBarBack.position.y = 1.5; // Above shark
        this.mesh.add(this.healthBarBack);
        
        // Health bar fill
        const fillMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
        this.healthBarFill = new THREE.Mesh(barGeometry.clone(), fillMaterial);
        this.healthBarFill.position.z = 0.01; // Slightly in front
        this.healthBarBack.add(this.healthBarFill);
    }
    
    updateHealthBar() {
        if (this.healthBarFill) {
            const healthPercent = this.health / this.maxHealth;
            this.healthBarFill.scale.x = healthPercent;
            this.healthBarFill.position.x = -(1 - healthPercent) / 2;
            
            // Color: green -> yellow -> red
            if (healthPercent > 0.5) {
                this.healthBarFill.material.color.setHex(0x00ff00);
            } else if (healthPercent > 0.25) {
                this.healthBarFill.material.color.setHex(0xffff00);
            } else {
                this.healthBarFill.material.color.setHex(0xff0000);
            }
            
            // Make health bar always face camera
            if (window.game && window.game.camera) {
                this.healthBarBack.lookAt(window.game.camera.position);
            }
        }
    }
    
    takeDamage(amount) {
        if (this.isDead) return;
        
        this.health -= amount;
        this.updateHealthBar();
        
        if (this.health <= 0 && !this.isDead) {
            this.die();
        }
    }
    
    die() {
        this.isDead = true;
        console.log('🦈 Shark killed! +50 essence');
        
        // Give player 50 essence
        if (window.game && window.game.player) {
            window.game.player.addEssence(50);
        }
        
        // Remove from scene
        this.scene.remove(this.mesh);
    }
    
    update(deltaTime, playerPosition) {
        if (this.isDead) return;
        
        // Update health bar
        this.updateHealthBar();
        
        // Keep shark underwater
        if (this.mesh.position.y > -1) {
            this.mesh.position.y = -1;
        }
        
        // Check if player is in water (below -0.5 y)
        const playerInWater = playerPosition.y < -0.5;
        
        if (playerInWater) {
            // Chase player
            const direction = new THREE.Vector3();
            direction.subVectors(playerPosition, this.mesh.position);
            const distance = direction.length();
            direction.normalize();
            
            // GREAT WHITE CHARGING MECHANIC
            if (this.type === 'greatwhite') {
                const chargeDistance = 25; // Start charging at 25 units
                if (distance < chargeDistance && !this.isCharging) {
                    this.isCharging = true;
                    this.speed = this.chargeSpeed; // FAST!
                } else if (distance >= chargeDistance && this.isCharging) {
                    this.isCharging = false;
                    this.speed = this.normalSpeed; // Back to slow
                }
            }
            
            // Move towards player
            this.mesh.position.add(direction.multiplyScalar(this.speed * deltaTime));
            
            // Keep underwater
            this.mesh.position.y = Math.min(this.mesh.position.y, -1);
            
            // Look at player
            const targetLookAt = playerPosition.clone();
            targetLookAt.y = this.mesh.position.y; // Same height
            this.mesh.lookAt(targetLookAt);
            
            // Attack if close
            if (distance < this.attackRange) {
                this.attack();
            }
        } else {
            // Patrol randomly in water
            const angle = Date.now() / 1000;
            this.mesh.position.x += Math.sin(angle * 0.5) * this.speed * deltaTime * 0.3;
            this.mesh.position.z += Math.cos(angle * 0.5) * this.speed * deltaTime * 0.3;
            
            // Keep in water area (don't go on land)
            const distFromCenter = Math.sqrt(
                this.mesh.position.x * this.mesh.position.x +
                this.mesh.position.z * this.mesh.position.z
            );
            
            if (distFromCenter < 140) {
                // Too close to island, swim away
                const awayAngle = Math.atan2(this.mesh.position.z, this.mesh.position.x);
                this.mesh.position.x = Math.cos(awayAngle) * 145;
                this.mesh.position.z = Math.sin(awayAngle) * 145;
            }
        }
        
        // Tail swish animation
        if (this.mesh.children[4]) { // Tail is 5th child
            this.mesh.children[4].rotation.y = Math.sin(Date.now() / 200) * 0.3;
        }
    }
    
    attack() {
        const currentTime = Date.now() / 1000;
        if (currentTime - this.lastAttackTime < this.attackCooldown) return;
        
        this.lastAttackTime = currentTime;
        
        // Damage player
        if (window.game && window.game.player) {
            window.game.player.takeDamage(this.attackDamage);
            console.log('🦈 Shark attacks!');
        }
    }
    
    destroy() {
        this.scene.remove(this.mesh);
    }
}

export class SharkManager {
    constructor(scene, player) {
        this.scene = scene;
        this.player = player;
        this.sharks = [];
        
        // Spawn sharks around ocean
        this.spawnSharks();
    }
    
    spawnSharks() {
        // Spawn 5-8 normal sharks in the ocean
        const numSharks = 5 + Math.floor(Math.random() * 4);
        
        for (let i = 0; i < numSharks; i++) {
            const angle = (i / numSharks) * Math.PI * 2 + Math.random();
            const radius = 150 + Math.random() * 100; // Far from island in ocean
            
            const position = new THREE.Vector3(
                Math.cos(angle) * radius,
                -1.5, // Underwater
                Math.sin(angle) * radius
            );
            
            const shark = new Shark(this.scene, position, 'normal');
            this.sharks.push(shark);
        }
        
        // Spawn 1-2 GREAT WHITES even further out
        const numGreatWhites = 1 + Math.floor(Math.random() * 2);
        for (let i = 0; i < numGreatWhites; i++) {
            const angle = Math.random() * Math.PI * 2;
            const radius = 280 + Math.random() * 80; // MUCH further out
            
            const position = new THREE.Vector3(
                Math.cos(angle) * radius,
                -2, // Deeper water
                Math.sin(angle) * radius
            );
            
            const greatWhite = new Shark(this.scene, position, 'greatwhite');
            this.sharks.push(greatWhite);
        }
        
        console.log(`🦈 ${numSharks} sharks spawned in the ocean!`);
        console.log(`🦈 ${numGreatWhites} GREAT WHITES lurk in the depths...`);
    }
    
    addShark() {
        // Add a new shark to the ocean
        const angle = Math.random() * Math.PI * 2;
        const radius = 150 + Math.random() * 100;
        
        const position = new THREE.Vector3(
            Math.cos(angle) * radius,
            -1.5, // Underwater
            Math.sin(angle) * radius
        );
        
        const shark = new Shark(this.scene, position);
        this.sharks.push(shark);
    }
    
    update(deltaTime) {
        this.sharks.forEach(shark => {
            shark.update(deltaTime, this.player.mesh.position);
        });
    }
}
